<template>
  <div class="space-y-4">
    <!-- Header com botão de fechar -->
    <div v-if="!uploadedImageUrl" class="flex justify-end">
      <button
        @click="$emit('close')"
        class="p-2 text-discord-light-400 hover:text-discord-light-100 hover:bg-discord-dark-200 rounded transition-colors"
        :title="t('uploadCrop.close')"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>
    <!-- Upload Area -->
    <div v-if="!imageUrl" class="border-2 border-dashed border-discord-dark-200 rounded p-6 text-center hover:border-discord-dark-100 transition-colors">
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        @change="handleFileSelect"
        class="hidden"
      />
      <div @click="$refs.fileInput.click()" class="cursor-pointer">
        <svg class="mx-auto h-12 w-12 text-discord-light-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
        </svg>
        <p class="mt-2 text-sm text-discord-light-600">
          {{ t('uploadCrop.clickUpload') }}
        </p>
        <p class="text-xs text-discord-light-500 mt-1">
          {{ t('uploadCrop.formatsHint') }}
        </p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="imageUrl && !croppedImage" class="space-y-4">
      <div class="text-center">
        <div class="flex items-center justify-center mb-4">
          <svg class="animate-spin h-8 w-8 text-discord-blurple" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <h3 class="text-lg font-medium text-discord-light-100 mb-2">{{ t('uploadCrop.preparingTitle') }}</h3>
        <p class="text-sm text-discord-light-600">{{ t('uploadCrop.preparingDesc') }}</p>
      </div>
    </div>

    <!-- Uploading State -->
    <div v-if="croppedImage && isUploading" class="space-y-4">
      <div class="text-center">
        <div class="flex items-center justify-center mb-4">
          <svg class="animate-spin h-8 w-8 text-discord-blurple" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <h3 class="text-xl font-semibold text-discord-light-100 mb-2">{{ t('uploadCrop.uploadingTitle') }}</h3>
        <p class="text-sm text-discord-light-600">{{ t('uploadCrop.uploadingDesc') }}</p>
      </div>
    </div>

    <!-- Success State -->
    <div v-if="uploadedImageUrl" class="space-y-6">
      <!-- Success Header -->
      <div class="text-center">
        <div class="flex items-center justify-center mb-4">
          <div class="success-icon">
            <svg class="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
        </div>
        <h3 class="text-xl font-semibold text-discord-light-100 mb-2">{{ t('uploadCrop.successTitle') }}</h3>
        <p class="text-sm text-discord-light-600">{{ t('uploadCrop.successDesc') }}</p>
      </div>
      
      <!-- Final Image Preview -->
      <div class="relative max-w-sm mx-auto">
        <div class="final-image-card">
          <div class="image-container">
            <img
              :src="uploadedImageUrl"
              class="final-image"
              :alt="t('uploadCrop.finalAlt')"
            />
            <div class="image-overlay">
              <div class="upload-badge">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                </svg>
                <span class="text-xs font-medium">{{ t('uploadCrop.uploaded') }}</span>
              </div>
            </div>
          </div>
          <div class="final-info">
            <div class="info-row">
              <span class="info-label">{{ t('uploadCrop.status') }}</span>
              <span class="status-success">{{ t('uploadCrop.completed') }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('uploadCrop.url') }}</span>
              <span class="info-url">{{ uploadedImageUrl.split('/').pop() }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Action Buttons -->
      <div class="flex space-x-3 justify-center">
        <button
          @click="resetImage"
          class="btn-secondary flex items-center space-x-2"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
          <span>{{ t('uploadCrop.changeImage') }}</span>
        </button>
        <button
          @click="$emit('close')"
          class="btn-primary flex items-center space-x-2"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
          <span>{{ t('uploadCrop.finish') }}</span>
        </button>
      </div>
    </div>

    <!-- Crop Modal -->
    <CropModal 
      v-if="showCropModal"
      :image-url="imageUrl"
      @close="closeCropModal"
      @cropped="handleCropped"
    />
  </div>
</template>

<script setup>
import { ref, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import api from '../services/api'
import { config, getFileUrl, isValidFileType, isValidFileSize } from '../config/app'
import CropModal from './CropModal.vue'

const props = defineProps({
  selectedFile: {
    type: File,
    default: null
  }
})

const emit = defineEmits(['image-uploaded', 'close'])

const { t } = useI18n()
const authStore = useAuthStore()

// Refs
const fileInput = ref(null)

// State
const imageUrl = ref('')
const croppedImage = ref('')
const uploadedImageUrl = ref('')
const isUploading = ref(false)
const showCropModal = ref(false)

const handleFileSelect = (event) => {
  const file = event.target.files[0]
  if (!file) return

  // Validate file type
  if (!isValidFileType(file)) {
    alert(t('uploadCrop.invalidType'))
    return
  }

  // Validate file size
  if (!isValidFileSize(file)) {
    alert(t('uploadCrop.maxSize', { mb: Math.round(config.upload.maxFileSize / 1024 / 1024) }))
    return
  }

  const reader = new FileReader()
  reader.onload = (e) => {
    imageUrl.value = e.target.result
    // Abre automaticamente o modal de crop após carregar a imagem
    openCropModal()
  }
  reader.readAsDataURL(file)
}

const openCropModal = () => {
  showCropModal.value = true
}

const closeCropModal = () => {
  showCropModal.value = false
}

const handleCropped = (croppedDataUrl) => {
  croppedImage.value = croppedDataUrl
  showCropModal.value = false
  // Upload automaticamente após o crop
  uploadImage()
}

const uploadImage = async () => {
  if (!croppedImage.value) return

  isUploading.value = true

  try {
    // Convert data URL to blob
    const response = await fetch(croppedImage.value)
    const blob = await response.blob()
    
    // Create form data
    const formData = new FormData()
    formData.append('file', blob, 'workspace-image.jpg')
    formData.append('type', 'workspace')

    // Upload to backend
    const uploadResponse = await api.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${authStore.token}`
      }
    })

    uploadedImageUrl.value = uploadResponse.data.url
    emit('image-uploaded', uploadResponse.data.url)
    
  } catch (error) {
    console.error('Erro ao fazer upload da imagem:', error)
    alert(t('uploadCrop.uploadError'))
  } finally {
    isUploading.value = false
  }
}

const resetImage = () => {
  imageUrl.value = ''
  croppedImage.value = ''
  uploadedImageUrl.value = ''
  showCropModal.value = false
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

// Watcher para arquivo selecionado via prop
watch(() => props.selectedFile, (newFile) => {
  if (newFile) {
    console.log('Arquivo recebido via prop:', newFile)
    handleFileSelect({ target: { files: [newFile] } })
  }
}, { immediate: true })

// Cleanup
onUnmounted(() => {
  if (imageUrl.value) {
    URL.revokeObjectURL(imageUrl.value)
  }
})
</script>

<style scoped>
/* Image preview */
.image-preview {
  position: relative;
  max-height: 300px;
  overflow: hidden;
  border-radius: 4px;
  background: #1a1a1a;
}

.preview-image {
  display: block;
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}

/* Success Icon */
.success-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(34, 197, 94, 0.1);
  border-radius: 50%;
  margin: 0 auto;
}

/* Image Preview Card */
.image-preview-card {
  background: #2a2a2a;
  border-radius: 4px;
  padding: 16px;
  border: 1px solid #3a3a3a;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.image-container {
  position: relative;
  width: 200px;
  height: 200px;
  margin: 0 auto 16px;
  border-radius: 4px;
  overflow: hidden;
  background: #1a1a1a;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.image-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
}

.success-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(34, 197, 94, 0.9);
  border-radius: 50%;
  backdrop-filter: blur(4px);
}

.image-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.info-label {
  font-size: 12px;
  color: #9ca3af;
  font-weight: 500;
}

.info-value {
  font-size: 12px;
  color: #f3f4f6;
  font-weight: 600;
}

/* Final Image Card */
.final-image-card {
  background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
  border-radius: 4px;
  padding: 20px;
  border: 1px solid #3a3a3a;
  box-shadow: 0 8px 25px -5px rgba(0, 0, 0, 0.3);
}

.final-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 4px;
}

.upload-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: rgba(34, 197, 94, 0.9);
  border-radius: 4px;
  backdrop-filter: blur(4px);
}

.final-info {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #3a3a3a;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
}

.status-success {
  color: #22c55e;
  font-weight: 600;
  font-size: 14px;
}

.info-url {
  font-size: 12px;
  color: #9ca3af;
  font-family: monospace;
  background: #1a1a1a;
  padding: 2px 6px;
  border-radius: 4px;
}
</style>
